import React, { useState, useEffect } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import {
  Alert,
  Spinner,
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
} from 'react-bootstrap';

const PublicApplicationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    date_of_application: new Date().toISOString().split('T')[0],
    dob: '',
    gender: 'Male',
    parents: '',
    father: '',
    mother: '',
    mobile: '',
    address: '',
    medium: 'English',
    school_name: '',
    class_applied: '',
  });

  const [classes] = useState([
    '8',
    '9',
    'SSLC (10)',
    'Plus One',
    'Plus Two',
    'CBSE 10',
  ]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');

  // Image Captcha
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaCorrect, setCaptchaCorrect] = useState(false);

  useEffect(() => {
    // Set date_of_application to current date on mount
    setFormData((prev) => ({
      ...prev,
      date_of_application: new Date().toISOString().split('T')[0],
    }));
    // Generate initial captcha
    generateImageCaptcha();
  }, []);

  // Generate new image captcha
  const generateImageCaptcha = () => {
    // Generate random 5-character alphanumeric code
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    setCaptchaAnswer('');
    setCaptchaCorrect(false);

    // Generate canvas image after state update
    setTimeout(() => drawCaptcha(code), 100);
  };

  // Draw captcha on canvas
  const drawCaptcha = (code) => {
    const canvas = document.getElementById('captcha-canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = 150;
    canvas.height = 50;

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add some noise lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `hsl(${Math.random() * 360}, 50%, 70%)`;
      ctx.lineWidth = Math.random() * 2;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Draw text
    ctx.font = 'bold 24px Arial';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < code.length; i++) {
      ctx.fillStyle = `hsl(${Math.random() * 360}, 60%, 30%)`;
      ctx.save();

      // Position and rotation for each character
      const x = 15 + i * 25;
      const y = 25 + (Math.random() - 0.5) * 10;
      const angle = (Math.random() - 0.5) * 0.4;

      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillText(code[i], 0, 0);
      ctx.restore();
    }

    // Add some dots
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = `hsla(${Math.random() * 360}, 50%, 50%, 0.3)`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        Math.random() * 3,
        0,
        2 * Math.PI,
      );
      ctx.fill();
    }
  };

  // Check captcha answer
  const checkCaptcha = (answer) => {
    const correct = answer.toUpperCase() === captchaCode;
    setCaptchaCorrect(correct);
    return correct;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!formData.dob) {
      errors.dob = 'Date of birth is required';
    }

    if (!formData.father.trim() && !formData.mother.trim()) {
      errors.parents = 'At least one parent name is required';
    }

    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.school_name.trim()) {
      errors.school_name = 'School name is required';
    }

    if (!formData.class_applied) {
      errors.class_applied = 'Please select a class to apply for';
    }

    if (!captchaAnswer || !captchaCorrect) {
      errors.captcha = 'Please solve the math problem correctly';
    }

    setFormErrors(errors);
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'father' || name === 'mother') {
      const updatedFormData = { ...formData, [name]: value };

      // Combine father and mother into parents field
      const fatherName = name === 'father' ? value : formData.father;
      const motherName = name === 'mother' ? value : formData.mother;
      const combinedParents = [fatherName, motherName]
        .filter((n) => n.trim())
        .join(', ');

      updatedFormData.parents = combinedParents;
      setFormData(updatedFormData);
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Clear error for this field when user starts typing
    if (formErrors[name] || formErrors.parents) {
      setFormErrors({ ...formErrors, [name]: '', parents: '' });
    }
  };

  const handleCaptchaChange = (e) => {
    const answer = e.target.value;
    setCaptchaAnswer(answer);

    if (answer) {
      checkCaptcha(answer);
    }

    if (formErrors.captcha) {
      setFormErrors({ ...formErrors, captcha: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setAlert({
        show: true,
        message: 'Please fix the errors in the form before submitting',
        variant: 'danger',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}apply-now`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success') {
        setApplicationNumber(
          response.data.data?.application_no ||
            'APP' + Date.now().toString().slice(-6),
        );
        setSubmissionSuccess(true);
        setAlert({ show: false, message: '', variant: '' });
      } else {
        setAlert({
          show: true,
          message:
            response.data.message ||
            'Failed to submit application. Please try again.',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error submitting application. Please try again later.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAgain = () => {
    setSubmissionSuccess(false);
    setApplicationNumber('');
    setFormData({
      name: '',
      date_of_application: new Date().toISOString().split('T')[0],
      dob: '',
      gender: 'Male',
      parents: '',
      father: '',
      mother: '',
      mobile: '',
      address: '',
      medium: 'English',
      school_name: '',
      class_applied: '',
    });
    setFormErrors({});
    setAlert({ show: false, message: '', variant: '' });
    setCaptchaAnswer('');
    setCaptchaCorrect(false);
    generateImageCaptcha();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        padding: '20px 0',
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            {/* Header */}
            <div className="text-center mb-4">
              <h1
                style={{
                  color: '#2c5aa0',
                  fontWeight: '700',
                  fontSize: '3rem',
                  marginBottom: '5px',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                }}
              >
                ILM CAMPUS
              </h1>
              <h2
                style={{
                  color: '#666',
                  fontWeight: '400',
                  fontSize: '1.3rem',
                  marginBottom: '10px',
                }}
              >
                Poonoor
              </h2>
              <h3
                style={{
                  color: '#333',
                  fontWeight: '500',
                  fontSize: '1.5rem',
                  marginBottom: '20px',
                }}
              >
                Application Form
              </h3>
            </div>

            {/* Success Message or Form */}
            {submissionSuccess ? (
              <Card
                style={{
                  borderRadius: '8px',
                  border: '1px solid #28a745',
                  backgroundColor: '#f8fff8',
                }}
              >
                <Card.Body style={{ padding: '50px', textAlign: 'center' }}>
                  <div
                    style={{
                      color: '#28a745',
                      fontSize: '4rem',
                      marginBottom: '20px',
                    }}
                  >
                    ✓
                  </div>
                  <h2 style={{ color: '#28a745', marginBottom: '20px' }}>
                    Application Submitted Successfully!
                  </h2>
                  <div style={{ fontSize: '1.2rem', marginBottom: '30px' }}>
                    <strong>Your Application Number:</strong>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: '#007bff',
                        margin: '15px 0',
                        padding: '15px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        display: 'inline-block',
                      }}
                    >
                      {applicationNumber}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: '1rem',
                      marginBottom: '40px',
                      color: '#666',
                    }}
                  >
                    <strong>Important:</strong> Please note down this
                    application number.
                    <br />
                    You will need this number while making the fee payment.
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleApplyAgain}
                    style={{
                      backgroundColor: '#007bff',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      padding: '12px 30px',
                    }}
                  >
                    Apply Again for Another Student
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <>
                {/* Alert Messages */}
                {alert.show && (
                  <Alert
                    variant={alert.variant}
                    dismissible
                    onClose={() => setAlert({ ...alert, show: false })}
                    className="mb-4"
                    style={{ borderRadius: '10px', border: 'none' }}
                  >
                    {alert.message}
                  </Alert>
                )}

                {/* Main Form Card */}
                <Card
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                  }}
                >
                  <Card.Body style={{ padding: '30px' }}>
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="lg"
                        />
                        <p className="mt-3 text-muted">
                          Submitting your application...
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        <Row>
                          {/* Left Column */}
                          <Col md={6}>
                            {/* Name */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Name of Student{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                isInvalid={!!formErrors.name}
                                style={{ borderRadius: '8px' }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.name}
                              </Form.Control.Feedback>
                            </Form.Group>

                            {/* Date of Birth */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Date of Birth{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="dob"
                                value={formData.dob}
                                onChange={handleChange}
                                isInvalid={!!formErrors.dob}
                                style={{ borderRadius: '8px' }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.dob}
                              </Form.Control.Feedback>
                            </Form.Group>

                            {/* Gender */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">Gender</Form.Label>
                              <Form.Select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                style={{ borderRadius: '8px' }}
                              >
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                              </Form.Select>
                            </Form.Group>

                            {/* Father */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Father's Name{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="father"
                                value={formData.father}
                                onChange={handleChange}
                                isInvalid={
                                  !!formErrors.parents &&
                                  !formData.father.trim() &&
                                  !formData.mother.trim()
                                }
                                style={{ borderRadius: '8px' }}
                              />
                            </Form.Group>

                            {/* Mother */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Mother's Name{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="mother"
                                value={formData.mother}
                                onChange={handleChange}
                                isInvalid={
                                  !!formErrors.parents &&
                                  !formData.father.trim() &&
                                  !formData.mother.trim()
                                }
                                style={{ borderRadius: '8px' }}
                              />
                              {formErrors.parents && (
                                <Form.Control.Feedback
                                  type="invalid"
                                  style={{ display: 'block' }}
                                >
                                  {formErrors.parents}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>

                            {/* Mobile */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Mobile <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="tel"
                                name="mobile"
                                value={formData.mobile}
                                onChange={handleChange}
                                isInvalid={!!formErrors.mobile}
                                style={{ borderRadius: '8px' }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.mobile}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          {/* Right Column */}
                          <Col md={6}>
                            {/* Address */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Address <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                isInvalid={!!formErrors.address}
                                style={{ borderRadius: '8px' }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.address}
                              </Form.Control.Feedback>
                            </Form.Group>

                            {/* Medium */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">Medium</Form.Label>
                              <Form.Select
                                name="medium"
                                value={formData.medium}
                                onChange={handleChange}
                                style={{ borderRadius: '8px' }}
                              >
                                <option value="English">English</option>
                                <option value="Malayalam">Malayalam</option>
                              </Form.Select>
                            </Form.Group>

                            {/* School Name */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                School <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="school_name"
                                value={formData.school_name}
                                onChange={handleChange}
                                isInvalid={!!formErrors.school_name}
                                style={{ borderRadius: '8px' }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formErrors.school_name}
                              </Form.Control.Feedback>
                            </Form.Group>

                            {/* Class Applied */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Class <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                name="class_applied"
                                value={formData.class_applied}
                                onChange={handleChange}
                                isInvalid={!!formErrors.class_applied}
                                style={{ borderRadius: '8px' }}
                              >
                                <option value="">Select class</option>
                                {classes.map((className) => (
                                  <option key={className} value={className}>
                                    {className}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {formErrors.class_applied}
                              </Form.Control.Feedback>
                            </Form.Group>

                            {/* Image Captcha */}
                            <Form.Group className="mb-4">
                              <Form.Label className="mb-2">
                                Security Verification{' '}
                                <span className="text-danger">*</span>
                              </Form.Label>

                              <Card className="p-3 bg-light">
                                <div className="d-flex align-items-center justify-content-center mb-3">
                                  <div style={{ textAlign: 'center' }}>
                                    <canvas
                                      id="captcha-canvas"
                                      style={{
                                        border: '2px solid #dee2e6',
                                        borderRadius: '8px',
                                        backgroundColor: '#fff',
                                      }}
                                    ></canvas>
                                    <div className="mt-2 small text-muted">
                                      Enter the code shown above
                                    </div>
                                  </div>
                                </div>

                                <div className="d-flex gap-2">
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter code"
                                    value={captchaAnswer}
                                    onChange={handleCaptchaChange}
                                    isInvalid={!!formErrors.captcha}
                                    isValid={captchaAnswer && captchaCorrect}
                                    style={{
                                      borderRadius: '8px',
                                      textAlign: 'center',
                                      textTransform: 'uppercase',
                                    }}
                                    maxLength={5}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    onClick={generateImageCaptcha}
                                    style={{ borderRadius: '8px' }}
                                    title="Generate new code"
                                  >
                                    🔄
                                  </Button>
                                </div>

                                {captchaAnswer && captchaCorrect && (
                                  <div className="text-success mt-2 small">
                                    ✓ Correct! Security verification passed.
                                  </div>
                                )}

                                {formErrors.captcha && (
                                  <div className="text-danger mt-2 small">
                                    {formErrors.captcha}
                                  </div>
                                )}
                              </Card>
                            </Form.Group>

                            {/* Submit Button */}
                            <div className="d-grid">
                              <Button
                                type="submit"
                                size="lg"
                                disabled={loading}
                                style={{
                                  backgroundColor: '#007bff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  padding: '12px',
                                }}
                              >
                                {loading ? (
                                  <>
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Submitting...
                                  </>
                                ) : (
                                  'Submit Application'
                                )}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </form>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicApplicationForm;
